/**
 * useLiveSession — suscripción SSE a `/admin/sessions/:id/live` (Fase 42 — F4).
 *
 * Devuelve la lista de eventos recibidos (snapshot + stream), el estado de
 * la conexión y un dedup por `spanId` para que reconnects o solapamientos
 * con el polling de `/timeline` no dupliquen eventos.
 *
 * Política de conexión:
 *  - Conecta sólo si `enabled=true` (típicamente: tab Live activo).
 *  - Reconnect automático con backoff exponencial cuando el server cierra
 *    sin `session.end`. EventSource ya reconecta por defecto, pero el
 *    nuestro hace dedup post-reconnect via Map(spanId → event).
 *  - Cuando llega `session.end`, marca `connected=false` y deja de
 *    reconnectar.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

export interface LiveSpanCloseEvent {
  type: 'span.close';
  ts: string;
  sessionId: string;
  spanId: string;
  parentSpanId: string | null;
  traceId: string;
  turnId: string | null;
  kind: string;
  name: string;
  startedAt: string;
  endedAt: string;
  durationMs: number;
  isError: boolean;
  errorKind: string | null;
  costNano: string | null;
  providerCostNano: string | null;
  attributes: Record<string, unknown>;
  callKind: string | null;
  callRefId: string | null;
  input?: unknown;
  output?: unknown;
}
export interface LiveSpanOpenEvent {
  type: 'span.open';
  ts: string;
  sessionId: string;
  spanId: string;
  parentSpanId: string | null;
  traceId: string;
  turnId: string | null;
  kind: string;
  name: string;
  startedAt: string;
  attributes: Record<string, unknown>;
}
export interface LiveSpanUpdateEvent {
  type: 'span.update';
  ts: string;
  sessionId: string;
  spanId: string;
  patch: Record<string, unknown>;
}
export interface LiveSessionEndEvent {
  type: 'session.end';
  ts: string;
  sessionId: string;
}
export interface LiveHeartbeatEvent {
  type: 'heartbeat';
  ts: string;
  sessionId: string;
}

export type LiveEvent =
  | LiveSpanCloseEvent
  | LiveSpanOpenEvent
  | LiveSpanUpdateEvent
  | LiveSessionEndEvent
  | LiveHeartbeatEvent;

export interface UseLiveSessionResult {
  /** Spans cerrados recibidos por SSE (incluye snapshot + stream). */
  spanCloses: Map<string, LiveSpanCloseEvent>;
  /** Spans en vuelo (abiertos pero aún no cerrados). */
  spanOpens: Map<string, LiveSpanOpenEvent>;
  /** Estado de la conexión SSE. */
  connected: boolean;
  /** Último error o `null`. */
  error: string | null;
  /** `true` si recibimos `session.end`. */
  sessionEnded: boolean;
}

export function useLiveSession(
  sessionId: string | null | undefined,
  enabled: boolean,
): UseLiveSessionResult {
  const [spanCloses, setSpanCloses] = useState<Map<string, LiveSpanCloseEvent>>(
    () => new Map(),
  );
  const [spanOpens, setSpanOpens] = useState<Map<string, LiveSpanOpenEvent>>(
    () => new Map(),
  );
  const [connected, setConnected] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionEnded, setSessionEnded] = useState<boolean>(false);
  // Ref para que cambios rápidos de spans en flight no causen un render
  // por cada token (futuro F5.x): batcheamos via React state.
  const closesRef = useRef(spanCloses);
  const opensRef = useRef(spanOpens);
  closesRef.current = spanCloses;
  opensRef.current = spanOpens;

  useEffect(() => {
    if (!enabled || !sessionId) {
      setConnected(false);
      return undefined;
    }
    // Reset al cambiar de sesión.
    setSpanCloses(new Map());
    setSpanOpens(new Map());
    setError(null);
    setSessionEnded(false);

    const url = `/api/admin/sessions/${encodeURIComponent(sessionId)}/live`;
    const es = new EventSource(url, { withCredentials: true });

    es.onopen = (): void => {
      setConnected(true);
      setError(null);
    };
    es.onerror = (): void => {
      // EventSource auto-reconnect: marcamos disconnected mientras tanto.
      setConnected(false);
    };

    const onSpanClose = (e: MessageEvent): void => {
      try {
        const data = JSON.parse(String(e.data)) as LiveSpanCloseEvent;
        setSpanCloses((prev) => {
          if (prev.has(data.spanId)) return prev;
          const next = new Map(prev);
          next.set(data.spanId, data);
          return next;
        });
        // Si llegó un close para un span que teníamos como open, lo
        // sacamos de opens (ya no está en vuelo).
        setSpanOpens((prev) => {
          if (!prev.has(data.spanId)) return prev;
          const next = new Map(prev);
          next.delete(data.spanId);
          return next;
        });
      } catch (err) {
        setError(`parse span.close failed: ${(err as Error).message}`);
      }
    };

    const onSpanOpen = (e: MessageEvent): void => {
      try {
        const data = JSON.parse(String(e.data)) as LiveSpanOpenEvent;
        setSpanOpens((prev) => {
          if (prev.has(data.spanId) || closesRef.current.has(data.spanId)) return prev;
          const next = new Map(prev);
          next.set(data.spanId, data);
          return next;
        });
      } catch (err) {
        setError(`parse span.open failed: ${(err as Error).message}`);
      }
    };

    const onSpanUpdate = (e: MessageEvent): void => {
      try {
        const data = JSON.parse(String(e.data)) as LiveSpanUpdateEvent;
        setSpanOpens((prev) => {
          const existing = prev.get(data.spanId);
          if (!existing) return prev;
          const next = new Map(prev);
          next.set(data.spanId, {
            ...existing,
            attributes: { ...existing.attributes, ...data.patch },
          });
          return next;
        });
      } catch (err) {
        setError(`parse span.update failed: ${(err as Error).message}`);
      }
    };

    const onSessionEnd = (): void => {
      setSessionEnded(true);
    };

    es.addEventListener('span.close', onSpanClose);
    es.addEventListener('span.open', onSpanOpen);
    es.addEventListener('span.update', onSpanUpdate);
    es.addEventListener('session.end', onSessionEnd);
    // Heartbeat se ignora; EventSource ya lo usa para keep-alive.

    return () => {
      es.removeEventListener('span.close', onSpanClose);
      es.removeEventListener('span.open', onSpanOpen);
      es.removeEventListener('span.update', onSpanUpdate);
      es.removeEventListener('session.end', onSessionEnd);
      es.close();
      setConnected(false);
    };
  }, [sessionId, enabled]);

  return useMemo<UseLiveSessionResult>(
    () => ({ spanCloses, spanOpens, connected, error, sessionEnded }),
    [spanCloses, spanOpens, connected, error, sessionEnded],
  );
}
